// components/UserProfile.js
import React, { useState, useEffect } from "react";
import { useUser } from "../../contexts/UserContext";
import AuthService from "../../services/AuthService";

const UserProfile = () => {
  const { user, loading, updateUser } = useUser(); // ✅ Use updateUser instead of refreshUser
  const [editField, setEditField] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize form data when user data loads
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  const handleEdit = (field) => {
    setEditField(field);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword =
        "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(formData.newPassword)) {
      newErrors.newPassword = "Password must contain at least one number";
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (field) => {
    try {
      setSaving(true);
      setErrors({});

      if (field === "name") {
        if (!formData.name.trim()) {
          setErrors({ name: "Name is required" });
          setSaving(false);
          return;
        }
        await AuthService.updateName(formData.name);
        // ✅ Update context immediately without full refresh
        updateUser({ name: formData.name });
      } else if (field === "email") {
        if (!formData.email.trim()) {
          setErrors({ email: "Email is required" });
          setSaving(false);
          return;
        }
        await AuthService.updateEmail(formData.email);
        // ✅ Update context immediately without full refresh
        updateUser({ email: formData.email });
      } else if (field === "password") {
        if (!validatePassword()) {
          setSaving(false);
          return;
        }
        await AuthService.updatePassword({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        });
        setFormData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
        // Password changes don't need user data update
      }

      setEditField(null);
      alert(
        `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } updated successfully.`
      );
    } catch (error) {
      console.error("Update error:", error);
      const serverError = error.response?.data?.errors;
      if (serverError) {
        setErrors(serverError);
      } else {
        alert(error.response?.data?.message || `Failed to update ${field}`);

        // If it's an authentication error, don't log out - just show message
        if (error.response?.status === 401) {
          console.warn(
            "Authentication error during update, but keeping user logged in"
          );
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditField(null);
    setErrors({});
    // Reset form data to original user values
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    }
  };

  if (loading)
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );

  return (
    <div className="container">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <h2 className="h5 mb-0">Login & Security</h2>
          <p className="text-muted mb-0 small">
            Manage your account credentials and security settings
          </p>
        </div>

        <div className="card-body">
          {/* Name Field */}
          <div className="mb-4 pb-3 border-bottom">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label fw-semibold">Name</label>
              {editField !== "name" ? (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleEdit("name")}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Edit
                </button>
              ) : (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleSave("name")}
                    disabled={saving}
                  >
                    {saving ? (
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                    ) : (
                      <i className="bi bi-check-lg me-1"></i>
                    )}
                    Save
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleCancel}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {editField !== "name" ? (
              <div className="d-flex align-items-center">
                <i className="bi bi-person text-muted me-2"></i>
                <span className="text-dark">{user?.name || "—"}</span>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <div className="invalid-feedback d-block">{errors.name}</div>
                )}
              </div>
            )}
          </div>

          {/* Email Field */}
          <div className="mb-4 pb-3 border-bottom">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label fw-semibold">Email Address</label>
              {editField !== "email" ? (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleEdit("email")}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Edit
                </button>
              ) : (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleSave("email")}
                    disabled={saving}
                  >
                    {saving ? (
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                    ) : (
                      <i className="bi bi-check-lg me-1"></i>
                    )}
                    Save
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleCancel}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {editField !== "email" ? (
              <div className="d-flex align-items-center">
                <i className="bi bi-envelope text-muted me-2"></i>
                <span className="text-dark">{user?.email || "—"}</span>
              </div>
            ) : (
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <div className="invalid-feedback d-block">{errors.email}</div>
                )}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label fw-semibold">Password</label>
              {editField !== "password" ? (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => handleEdit("password")}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Change
                </button>
              ) : (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleSave("password")}
                    disabled={saving}
                  >
                    {saving ? (
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></span>
                    ) : (
                      <i className="bi bi-check-lg me-1"></i>
                    )}
                    Update
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleCancel}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {editField !== "password" ? (
              <div className="d-flex align-items-center">
                <i className="bi bi-shield-lock text-muted me-2"></i>
                <span className="text-dark">••••••••</span>
              </div>
            ) : (
              <div className="bg-light p-3 rounded">
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.currentPassword ? "is-invalid" : ""
                    }`}
                    placeholder="Enter your current password"
                  />
                  {errors.currentPassword && (
                    <div className="invalid-feedback d-block">
                      {errors.currentPassword}
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.newPassword ? "is-invalid" : ""
                    }`}
                    placeholder="Enter your new password"
                  />
                  {errors.newPassword && (
                    <div className="invalid-feedback d-block">
                      {errors.newPassword}
                    </div>
                  )}
                  <div className="form-text">
                    Password must be at least 8 characters with one uppercase
                    letter and one number
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.confirmPassword ? "is-invalid" : ""
                    }`}
                    placeholder="Confirm your new password"
                  />
                  {errors.confirmPassword && (
                    <div className="invalid-feedback d-block">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
